import React, { useCallback, useMemo } from 'react';

// Material UI
import Checkbox from '@material-ui/core/Checkbox';
import Tooltip from '@material-ui/core/Tooltip';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

// Components
import { MEMBER_GROUP_ROLES, MemberListType, ROLE_LABELS, ROLE_SUBSTITUTES, ROLES } from '../../../../_configuration';
import { SimpleTable } from '../../_core/_ui/simple-table.component';
import { HeaderWithTooltip } from '../../_core/_ui/header-with-tooltip.component';
import { IMemberList } from './member-list.types';
import { getCurrMember } from '../../../lib/services/member.service';
import { areAllRolesOn, toggleAllRoles } from './member-list.helpers';
import { ASSESSMENT_ONLY_AGE_GROUP_COLUMNS } from './member-list.consts';


interface IAgeGroupMemberList extends Pick<IMemberList, 'updateMemberRoles' | 'isSavingRoles' | 'toggleRole' | 'currMember' | 'assessmentId'> {
  ageGroups: IAgeGroup[];
  assessments: IAssessment[];
}

export const AgeGroupMemberList: React.FC<IAgeGroupMemberList> = ({
  updateMemberRoles,
  isSavingRoles,
  ageGroups,
  assessments,
  currMember: selectedMember,
  toggleRole,
  assessmentId
}) => {
  const { member: currMember } = getCurrMember();

  const ageGroupPerms = MEMBER_GROUP_ROLES[MemberListType.AGE_GROUP];

  // Find the current assessment (use the first if none selected)
  const currentAssessment = useMemo(() => assessmentId ? assessments.find(a => a._id === assessmentId) : null, [assessments, assessmentId]);

  // Filter age groups to those belonging to the current assessment
  const filteredAgeGroups = useMemo(() => currentAssessment
    ? ageGroups.filter(ag => ag.assessmentId === currentAssessment._id)
    : [], [ageGroups, currentAssessment]);

  // Helper to get roles for an age group (from the selected member, or empty)
  const getRolesForAgeGroup = useCallback((ageGroupId: string) => {
    if (!selectedMember) return [];
    const found = selectedMember.rolesByAgeGroup.find(r => r.ageGroupId === ageGroupId);

    return found ? found.roles : [];
  }, [selectedMember]);

  // Helper to get assessment-level roles for the current assessment
  const getAssessmentRoles = useCallback(() => {
    if (!selectedMember || !currentAssessment) return [];
    const found = selectedMember.rolesByAssessment.find(r => r.assessmentId === currentAssessment._id);
    return found ? found.roles : [];
  }, [selectedMember, currentAssessment]);

  // Helper to check if a role should be locked based on higher-level permissions
  const isRoleLocked = useCallback((role: string) => {
    const member = selectedMember;
    if (!member) return false;
    if (role === ROLES.USER_MANAGEMENT && member._id === currMember._id) return true;

    // Check if the user has higher-level permissions that would lock this role
    const roleSubstitutes = ROLE_SUBSTITUTES[MemberListType.AGE_GROUP][role];
    if (!roleSubstitutes) return false;

    // Check if the user has association-level (super user) permissions
    if (roleSubstitutes[MemberListType.SUPER]) {
      const requiredSuperRoles = roleSubstitutes[MemberListType.SUPER];
      if (requiredSuperRoles && (member.isAdmin || member.superUserRoles?.some(userRole => requiredSuperRoles.includes(userRole)))) {
        return true;
      }
    }

    // Check if the user has assessment-level permissions
    if (roleSubstitutes[MemberListType.ASSESSMENT]) {
      const requiredAssessmentRoles = roleSubstitutes[MemberListType.ASSESSMENT];
      const userAssessmentRoles = member.rolesByAssessment.find(r => r.assessmentId === currentAssessment?._id)?.roles || [];
      if (requiredAssessmentRoles && userAssessmentRoles.some(userRole => requiredAssessmentRoles.includes(userRole))) {
        return true;
      }
    }

    return false;
  }, [selectedMember, currentAssessment?._id, currMember._id]);

  // Helper to get the lock reason for a role
  const getLockReason = useCallback((role: string) => {
    const member = selectedMember;
    if (!member) return '';
    if (role === ROLES.USER_MANAGEMENT && member._id === currMember._id) return 'Cannot lock yourself out of user management';

    const roleSubstitutes = ROLE_SUBSTITUTES[MemberListType.AGE_GROUP][role];
    if (!roleSubstitutes) return '';

    const reasons = [];

    // Check association-level permissions
    if (roleSubstitutes[MemberListType.SUPER]) {
      const requiredSuperRoles = roleSubstitutes[MemberListType.SUPER];
      if (requiredSuperRoles) {
        if (member.isAdmin) {
          reasons.push(`Admin privileges grant access to all roles`);
        } else {
          const matchingSuperRoles = member.superUserRoles?.filter(userRole => requiredSuperRoles.includes(userRole)) ?? [];
          if (matchingSuperRoles.length > 0) {
            reasons.push(`Association-level ${matchingSuperRoles.map(r => ROLE_LABELS[r] || r).join(', ')}`);
          }
        }
      }
    }

    // Check assessment-level permissions
    if (roleSubstitutes[MemberListType.ASSESSMENT]) {
      const requiredAssessmentRoles = roleSubstitutes[MemberListType.ASSESSMENT];
      if (requiredAssessmentRoles) {
        const userAssessmentRoles = member.rolesByAssessment.find(r => r.assessmentId === currentAssessment?._id)?.roles || [];
        const matchingAssessmentRoles = userAssessmentRoles.filter(userRole => requiredAssessmentRoles.includes(userRole));
        if (matchingAssessmentRoles.length > 0) {
          reasons.push(`Assessment-level ${matchingAssessmentRoles.map(r => ROLE_LABELS[r] || r).join(', ')}`);
        }
      }
    }

    return reasons.length > 0 ? `This permission is granted by ${reasons.join(' and ')} permission(s)` : '';
  }, [selectedMember, currentAssessment?._id, currMember._id]);

  const headerRows = [
    {
      cells: [
        { content: "Age Group", colSpan: 1, borderRight: true },
        { content: "All", colSpan: 1, borderRight: true },
        ...ageGroupPerms.map(role => ({
          content: <HeaderWithTooltip
            label={ROLE_LABELS[role] || role}
            role={role}
          />,
          colSpan: 1
        })),
        ...ASSESSMENT_ONLY_AGE_GROUP_COLUMNS.map(role => ({
          content: <HeaderWithTooltip
            label={ROLE_LABELS[role] || role}
            role={role}
          />,
          colSpan: 1
        })),
      ]
    },
  ]


  const rows = useMemo(() => filteredAgeGroups.map(ag => {
    const roles = getRolesForAgeGroup(ag._id);
    const assessmentRoles = getAssessmentRoles();
    const ageGroupRoleGroup = { roles, groupRoles: ageGroupPerms, isLocked: isRoleLocked };
    const allChecked = areAllRolesOn(ageGroupRoleGroup);
    const allLocked = ageGroupPerms.every(role => isRoleLocked(role));
    const someChecked = roles.some(role => ageGroupPerms.includes(role)) || ageGroupPerms.some(role => isRoleLocked(role));
    const saving = selectedMember ? isSavingRoles({ memberId: selectedMember._id, ageGroupId: ag._id }) : false;

    return {
      cells: [
        { content: ag.name, borderRight: true },
        { content:
          <Tooltip title={allLocked ? "All permissions are granted by higher-level permissions" : ""} arrow>
            <span>
              <Checkbox
                checked={allChecked}
                indeterminate={!allChecked && someChecked}
                disabled={allLocked || saving}
                onChange={() => {
                  if (selectedMember) updateMemberRoles({ member: selectedMember, ageGroupId: ag._id, roles: toggleAllRoles(ageGroupRoleGroup) });
                }}
              />
            </span>
          </Tooltip>
        , borderRight: true },
        ...ageGroupPerms.map(role => {
          const isLocked = isRoleLocked(role);
          const isChecked = roles.includes(role) || isLocked;
          const lockReason = getLockReason(role);

          return { content:
            <Tooltip title={isLocked ? lockReason : ""} arrow>
              <span>
                <Checkbox
                  checked={isChecked}
                  disabled={isLocked || saving}
                  onChange={() => {
                    if (isLocked) return;
                    if (selectedMember) toggleRole({member: selectedMember, role, ageGroupId: ag._id})
                  }}
                />
              </span>
            </Tooltip>
          };
        }),
        ...ASSESSMENT_ONLY_AGE_GROUP_COLUMNS.map(role => {
          const hasAssessmentRole = assessmentRoles.includes(role);
          const tooltipText = `This permission is managed at the assessment level${hasAssessmentRole ? ' and is currently granted' : ''}`;

          return { content:
            <Tooltip title={tooltipText} arrow>
              <span>
                <Checkbox
                  checked={hasAssessmentRole}
                  disabled={true}
                />
              </span>
            </Tooltip>
          };
        }),
      ],
    }
  }), [filteredAgeGroups, ageGroupPerms, selectedMember, updateMemberRoles, isSavingRoles, toggleRole, getRolesForAgeGroup, getAssessmentRoles, isRoleLocked, getLockReason])

  return (
    <div id="age-group-permissions-table">
      {selectedMember && filteredAgeGroups.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <Typography variant="body2" color="textSecondary">
            No age groups found
          </Typography>
        </Box>
      ) : selectedMember ? (
        <SimpleTable headerRows={headerRows} rows={rows} stickyHeader stickyFirstColumn columnMinWidths />
      ) : null}
    </div>
  )
}
